import axios from 'axios';
import { useTranslation } from 'react-i18next';
import useErrorStore, { setErrorActionSelector } from 'Core/stores/error';
import { useQuery } from 'react-query';
import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { get } from 'Core/utils/platformApi';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import { getUrlParam } from 'Core/utils/utils';

async function getCpsCardsSilently(customerId, externalReferenceId) {
  try {
    const { data } = await axios.get(
      `${process.env.API_PLATFORM_URL}/cards/cps_profile_gid/${customerId}`,
      {
        headers: {
          web_code: getUrlParam('web-code'),
          payment_id: getUrlParam('payment-id'),
          external_reference_id: externalReferenceId,
        },
      }
    );
    return data;
  } catch {
    return null;
  }
}

function mapCards(data) {
  return data.map((method) => ({
    card: {
      brand: method.brand.toLowerCase(),
      last4: method.last4digit,
    },
    paymentMethodId: method.payment_method_id,
    preferred: method.preferred,
    type: 'card',
  }));
}

function useGetSavedCardsDefault(enabled) {
  const { t } = useTranslation();
  const setError = useErrorStore(setErrorActionSelector);
  const { data: paymentInfo } = useGetPaymentInfo();

  return useQuery(
    cacheKeys.savedCards,
    () =>
      get('/cards', {
        headers: { external_reference_id: paymentInfo.external_reference_id },
      }),
    {
      onError(e) {
        if (e) {
          setError(t('status.error.getSavedCardsFailed') + ` - (${e.message})`);
        }
      },
      enabled: enabled && Boolean(paymentInfo),
      select: mapCards,
    }
  );
}

function useGetSavedCardsCps(enabled) {
  const { t } = useTranslation();
  const setError = useErrorStore(setErrorActionSelector);
  const { data: paymentInfo } = useGetPaymentInfo();

  return useQuery(
    [cacheKeys.savedCards, 'cps', paymentInfo?.customer_id],
    async () => {
      const cpsData = await getCpsCardsSilently(
        paymentInfo.customer_id,
        paymentInfo.external_reference_id
      );
      if (!cpsData || cpsData.length === 0) {
        return get('/cards', {
          headers: { external_reference_id: paymentInfo.external_reference_id },
        });
      }
      return cpsData;
    },
    {
      onError(e) {
        if (e) {
          setError(t('status.error.getSavedCardsFailed') + ` - (${e.message})`);
        }
      },
      enabled: enabled && Boolean(paymentInfo?.customer_id),
      select: mapCards,
    }
  );
}

export function useGetSavedCards() {
  const useCps = getUrlParam('cps_search') !== 'false';
  const cpsQuery = useGetSavedCardsCps(useCps);
  const defaultQuery = useGetSavedCardsDefault(!useCps);
  return useCps ? cpsQuery : defaultQuery;
}
